// src/ecrans/accueil/BlocRemplissage.jsx — « Remplissage » de l'Accueil NEST
// (maquette v819), pleine largeur : les 30 prochains jours, le % du parc et
// les nuits libres (règle des chiffres, src/ecrans/couverture.js), une frise
// par logement (Airbnb / Booking / Direct / libre), et la carte « N annonces
// à démarcher ». PAS de « € réservés » : aucun montant ici (chemin d'argent).
//
// Un logement non suivi (sans source, source endormie) garde sa ligne, mais
// son pourcentage vaut « — » et ses nuits sans séjour sont hachurées : on ne
// sait pas si elles sont libres.

import { useState } from "react";
import { EtatLecture, jourCourt, mot } from "../nest";
import { chiffreSuivi } from "../couverture";
import { IcBulle } from "./accueil-style";
import CartePliee, { Carte } from "./CartePliee";
import { remplissage } from "./accueil-remplissage";

const VISIBLES = 5;

/** Les lectures en cours ou ratées dictent « … » ou « — » avant la règle des chiffres. */
function chiffre(lectures, n, couverture) {
  if (lectures.some((l) => !l || l.etat === "lecture")) return chiffreSuivi("…", couverture);
  if (lectures.some((l) => l.etat !== "ok")) return chiffreSuivi(NaN, couverture);
  return chiffreSuivi(n, couverture);
}

export default function BlocRemplissage({ logements, sejours, couverture, du, au, prospection, onAller, plie }) {
  const [tous, setTous] = useState(false);
  const r =
    logements.etat === "ok" && sejours.etat === "ok"
      ? remplissage({ logements: logements.data.logements || [], sejours: sejours.data.sejours || [], du, au, couverture })
      : null;
  const lectures = [logements, sejours];
  const pc = chiffre(lectures, r && r.parc ? r.parc.rempli ?? NaN : NaN, couverture);
  const libres = chiffre(lectures, r && r.parc ? r.parc.libres ?? NaN : NaN, couverture);
  const sous = `30 prochains jours · du ${jourCourt(du)} au ${jourCourt(r ? r.jours[r.jours.length - 1] : du)}`;
  const pcTexte = typeof pc.valeur === "number" ? `${pc.valeur} %` : pc.valeur;
  // La limite ne porte que sur les logements suivis : un logement non suivi
  // garde toujours sa ligne « — » visible, après les suivis.
  const suivis = r ? r.lignes.filter((l) => l.suivi) : [];
  const lignes = r ? (tous ? r.lignes : [...suivis.slice(0, VISIBLES), ...r.lignes.filter((l) => !l.suivi)]) : [];
  const nProsp =
    prospection.etat === "ok" ? Number(prospection.data.aRegarder ?? prospection.data.total) : NaN;
  const prosp = prospection.etat === "lecture" ? "…" : Number.isFinite(nProsp) ? nProsp : "—";

  const resume = (
    <>
      <span className="nacc-pli-big">
        <b>{pcTexte}</b>
        <span>
          du parc rempli · {libres.valeur} {mot(libres.valeur, "nuit libre", "nuits libres")}
          {pc.sur ? ` · ${pc.sur}` : ""}
        </span>
      </span>
      {r && r.parJour && pc.valeur !== "—" ? (
        <span className="nacc-spark" aria-hidden="true">
          {r.parJour.map((x, i) => (
            <i key={r.jours[i]} className={x < 0.5 ? "nacc-bas" : ""} style={{ height: `${Math.max(5, Math.round(x * 100))}%` }} />
          ))}
        </span>
      ) : null}
      {r && r.plusLibre && r.plusLibre.rempli !== null ? (
        <span className="nacc-pli-next">
          Le plus libre : {r.plusLibre.nom} · {r.plusLibre.rempli} %
        </span>
      ) : null}
    </>
  );

  return (
    <CartePliee plie={plie} titre="Remplissage" ic="calendrier" resume={resume} testId="accueil-remplissage">
      {(onReplier) => (
        <Carte
          titre="Remplissage"
          sous={sous}
          onReplier={onReplier}
          testId="accueil-remplissage"
          action={
            suivis.length > VISIBLES ? (
              <button type="button" className="nacc-lien" onClick={() => setTous((v) => !v)} aria-expanded={tous}>
                {tous ? "Réduire" : "Tous les logements"}
              </button>
            ) : null
          }
        >
          <EtatLecture lecture={logements.etat === "echec" ? logements : null} quoi="Les logements" />
          <EtatLecture lecture={sejours.etat === "echec" ? sejours : null} quoi="Les séjours" />
          <div className="nacc-big" data-testid="accueil-remplissage-parc">
            <b>{pcTexte}</b>
            <span>
              du parc rempli{pc.sur ? ` · ${pc.sur}` : ""}
              <br />
              {libres.valeur} {mot(libres.valeur, "nuit libre", "nuits libres")}
            </span>
          </div>
          {lignes.map((l) => (
            <div key={l.id} className="nacc-rr" data-testid={`accueil-remplissage-${l.id}`}>
              <span className="nacc-nm" title={l.nom}>
                {l.nom}
                {!l.suivi && l.connue ? <small>non suivi : aucune source vivante</small> : null}
              </span>
              <span className={`nacc-frise${l.suivi ? "" : " ns"}`} aria-hidden="true">
                {l.nuits.map((p, i) => (
                  <i key={r.jours[i]} className={p || ""} />
                ))}
              </span>
              <span className={`nacc-pc${l.rempli !== null && l.rempli < 50 ? " nacc-bas" : ""}`}>
                {l.rempli === null ? "—" : `${l.rempli} %`}
              </span>
            </div>
          ))}
          {r ? (
            <div className="nacc-lg">
              <span><i className="airbnb" />Airbnb</span>
              <span><i className="booking" />Booking</span>
              <span><i className="direct" />Direct</span>
              <span><i />libre</span>
            </div>
          ) : null}
          <button type="button" className="nacc-pros" onClick={() => onAller && onAller("logements")} data-testid="accueil-prospection">
            <IcBulle ic="maison" />
            <span className="nacc-tx">
              <b>
                {prosp} {mot(prosp, "annonce à démarcher", "annonces à démarcher")}
              </b>
              <span>Logements › À démarcher</span>
            </span>
            <span className="o">Ouvrir</span>
          </button>
          <EtatLecture lecture={prospection.etat === "echec" ? prospection : null} quoi="Prospection" />
        </Carte>
      )}
    </CartePliee>
  );
}
